import React, { forwardRef } from 'react';
import { cn } from '@/utils';
import type { TypographyColor } from './typography-color.generated';

// Re-export so consumers can import TypographyColor from this file or from the barrel.
export type { TypographyColor } from './typography-color.generated';

// ---------------------------------------------------------------------------
// Variant → size map (mirrors mobile styles.ts)
// ---------------------------------------------------------------------------

export type TypographyVariantSizeMap = {
  display: 'huge' | 'large' | 'medium';
  header: 'h1' | 'h2' | 'h3' | 'h4';
  body: 'large' | 'medium' | 'small' | 'xs';
  sectionHeader: 'medium';
  button: 'large' | 'medium' | 'small';
  formLabel: 'default';
  numeric: 'medium' | 'large';
};

export type TypographyVariant = keyof TypographyVariantSizeMap;

// Derive a union of all valid {variant, size} pairs — enforces correct
// combinations at the call site via TypeScript discriminated union.
export type TypographyVariantSizePair = {
  [V in TypographyVariant]: {
    variant: V;
    size: TypographyVariantSizeMap[V];
  };
}[TypographyVariant];

// ---------------------------------------------------------------------------
// Color
// ---------------------------------------------------------------------------

/**
 * `TypographyColor` is auto-generated from the `--text-color-*` CSS custom
 * properties in `src/primer/tokens/tailwind.css` (Supernova-generated) by
 * `vite-plugins/generate-typography-types.ts`.
 *
 * The type is re-generated automatically on every `vite dev` / `vite build`
 * start, and live-updated during dev whenever `tailwind.css` changes (e.g.
 * after a Supernova token update). The generated file is committed to source
 * control so `tsc` and the editor LSP always have a current type without
 * needing Vite running.
 *
 * Do not edit `typography-color.generated.ts` by hand — changes will be
 * overwritten on the next dev server or build start.
 *
 * See `vite-plugins/generate-typography-types.ts` for the exclusion list
 * and the rationale behind any excluded tokens.
 */

// ---------------------------------------------------------------------------
// Tailwind class maps
// ---------------------------------------------------------------------------

/**
 * Maps each variant/size pair to the corresponding Tailwind component class
 * defined in `src/primer/tokens/tailwind.css` (e.g. `body-medium`, `header-h1`).
 *
 * These class name strings are **not type-checked by the compiler** — if a
 * token is renamed in the Supernova-generated file the class will silently stop
 * applying. When updating token files, verify that every key here still matches
 * a class defined in `tailwind.css`.
 */

const typographyClassMap: {
  [V in TypographyVariant]: Record<TypographyVariantSizeMap[V], string>;
} = {
  display: {
    huge: 'display-huge',
    large: 'display-large',
    medium: 'display-medium',
  },
  header: {
    h1: 'header-h1',
    h2: 'header-h2',
    h3: 'header-h3',
    h4: 'header-h4',
  },
  body: {
    large: 'body-large',
    medium: 'body-medium',
    small: 'body-small',
    xs: 'body-xs',
  },
  sectionHeader: {
    medium: 'section-header-medium',
  },
  button: {
    large: 'button-large',
    medium: 'button-medium',
    small: 'button-small',
  },
  formLabel: {
    default: 'form-label',
  },
  numeric: {
    medium: 'numeric-medium',
    large: 'numeric-large',
  },
};

/**
 * Maps each `TypographyColor` value to the Tailwind `text-*` utility class
 * that resolves through `src/primer/utilities.css` to the correct
 * `--text-color-*` token.
 *
 * Like `typographyClassMap`, these strings are not type-checked — a renamed
 * token causes a silent no-op. Keep in sync with `utilities.css` and
 * `TypographyColor` above.
 */
const colorClassMap: Record<TypographyColor, string> = {
  header: 'text-header',
  body: 'text-body',
  bodylight: 'text-bodylight',
  bodylighter: 'text-bodylighter',
  white: 'text-white',
  interactive: 'text-interactive',
  interactivehover: 'text-interactive-hover-text',
  disabled: 'text-disabled',
  error: 'text-error',
  success: 'text-success',
  warning: 'text-warning',
  info: 'text-info',
};

// ---------------------------------------------------------------------------
// Default element + color per variant
// ---------------------------------------------------------------------------

const defaultElementMap: Record<TypographyVariant, React.ElementType> = {
  display: 'p',
  header: 'h2',
  body: 'p',
  sectionHeader: 'p',
  button: 'span',
  formLabel: 'label',
  numeric: 'span',
};

const defaultColorMap: Record<TypographyVariant, TypographyColor> = {
  display: 'header',
  header: 'header',
  body: 'body',
  sectionHeader: 'bodylight',
  button: 'header',
  formLabel: 'body',
  numeric: 'body',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Text alignment options for the Typography component. */
export type TypographyAlign = 'left' | 'center' | 'right';

const alignClassMap: Record<TypographyAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/**
 * Text decoration options for the Typography component.
 *
 * Each key is an independent toggle — combine freely without combinatorial
 * enum values. New decorations can be added here without changing call sites
 * that don't use them.
 *
 * @example
 * <Typography variant="body" size="medium" decoration={{ bold: true, underline: true }}>
 *   Bold and underlined
 * </Typography>
 */
export type TypographyDecoration = {
  bold?: boolean;
  underline?: boolean;
  italic?: boolean;
};

function resolveDecorationClasses(decoration: TypographyDecoration): string {
  const classes: string[] = [];
  if (decoration.bold) classes.push('font-semibold');
  if (decoration.underline) classes.push('underline');
  if (decoration.italic) classes.push('italic');
  return cn(classes);
}

// Base own props without the polymorphic `as` — used to build the full type.
type TypographyBaseProps = TypographyVariantSizePair & {
  color?: TypographyColor;
  /** Text alignment. Omit to inherit alignment from the parent element. */
  align?: TypographyAlign;
  /** Text decoration. Prefer this over `className` for bold/underline/italic styling. */
  decoration?: TypographyDecoration;
  className?: string;
  children?: React.ReactNode;
};

export type TypographyProps<E extends React.ElementType = 'p'> = TypographyBaseProps & {
  /** Override the rendered HTML element. Defaults to a semantic element for the variant. */
  as?: E;
} & Omit<React.ComponentPropsWithoutRef<E>, keyof TypographyBaseProps | 'as'>;

// ---------------------------------------------------------------------------
// Internal props used by the forwardRef implementation (widened for TS)
// ---------------------------------------------------------------------------

interface InternalProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  variant: TypographyVariant;
  size: string;
  color?: TypographyColor;
  align?: TypographyAlign;
  decoration?: TypographyDecoration;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Design System Typography Component
 *
 * Enforces valid variant/size combinations from the Primer design system.
 * Renders a semantic HTML element by default; use the `as` prop to override.
 *
 * Default elements per variant:
 * - `display`       → `<p>`
 * - `header`        → `<h2>` (override with `as="h1"` etc. for correct heading level)
 * - `body`          → `<p>`
 * - `sectionHeader` → `<p>`
 * - `button`        → `<span>`
 * - `formLabel`     → `<label>`
 * - `numeric`       → `<span>`
 *
 * @example
 * <Typography variant="header" size="h1">Page Title</Typography>
 * <Typography variant="body" size="medium" color="bodylight">Some copy</Typography>
 * <Typography variant="body" size="large" as="span">Inline text</Typography>
 * <Typography variant="display" size="huge" as="h1">Splash heading</Typography>
 */
const TypographyInner = forwardRef<HTMLElement, InternalProps>(
  ({ as, variant, size, color, align, decoration, className, children, ...rest }, ref) => {
    const Element: React.ElementType = as ?? defaultElementMap[variant];
    const typographyClass = (typographyClassMap[variant] as Record<string, string>)[size];
    const resolvedColor: TypographyColor = color ?? defaultColorMap[variant];
    const colorClass = colorClassMap[resolvedColor];
    const alignClass = align ? alignClassMap[align] : undefined;
    const decorationClass = decoration ? resolveDecorationClasses(decoration) : undefined;

    return (
      <Element
        ref={ref}
        className={cn(typographyClass, colorClass, alignClass, decorationClass, className)}
        {...rest}
      >
        {children}
      </Element>
    );
  }
);

TypographyInner.displayName = 'Typography';

// Cast to the fully-typed polymorphic signature so call sites get correct
// inference while the internal implementation uses the widened InternalProps.
const Typography = TypographyInner as <E extends React.ElementType = 'p'>(
  props: TypographyProps<E> & { ref?: React.Ref<HTMLElement> }
) => React.ReactElement | null;

export default Typography;
